/**
 * SpecialtySwitcher
 * ──────────────────────────────────────────────────────────
 * Compact dropdown that lives in the Clinical Focus top bar.
 * Lets the demo user swap specialties on the fly — every
 * specialty-aware panel (vocab, CPTs, Clinical Signals, and the
 * mode-switched MSE panel for Psychiatry) updates immediately.
 *
 * Marked as DEMO-ONLY: render only when `isDemo` is true.
 */
import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Stethoscope, Check } from 'lucide-react';
import { useSpecialty } from '../../../contexts/SpecialtyContext';

export const SpecialtySwitcher = () => {
  const { specialtyId, specialty, setSpecialtyId, specialties } = useSpecialty();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  return (
    <div ref={ref} className="relative" data-testid="specialty-switcher">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="h-7 px-2.5 inline-flex items-center gap-1.5 rounded-full bg-white/10 hover:bg-white/15 border border-white/20 text-white text-[11px] font-medium transition"
        data-testid="specialty-switcher-btn"
        title="Switch specialty (demo)"
      >
        <Stethoscope className="h-3 w-3 text-white/70" />
        <span className="max-w-[130px] truncate">{specialty.label}</span>
        <ChevronDown className={`h-3 w-3 text-white/60 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-1.5 w-[260px] max-h-[480px] overflow-y-auto rounded-xl bg-white border border-slate-200 shadow-xl z-50"
          data-testid="specialty-switcher-menu"
        >
          <div className="px-3 py-2 border-b border-slate-100 bg-slate-50">
            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Demo · Specialty</p>
            <p className="text-[10px] text-slate-400 mt-0.5">Vocab, CPTs, and panels update instantly</p>
          </div>
          <div className="py-1">
            {specialties.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => { setSpecialtyId(s.id); setOpen(false); }}
                className={`w-full flex items-start gap-2 px-3 py-2 text-left hover:bg-slate-50 transition ${
                  specialtyId === s.id ? 'bg-slate-50' : ''
                }`}
                data-testid={`specialty-opt-${s.id}`}
              >
                <Check className={`h-3 w-3 mt-0.5 shrink-0 ${specialtyId === s.id ? 'text-slate-900' : 'text-transparent'}`} />
                <div className="min-w-0 flex-1">
                  <p className="text-[12px] font-medium text-slate-900 truncate">{s.label}</p>
                  <p className="text-[10px] text-slate-500 truncate">{s.tagline}</p>
                </div>
                {s.mode !== 'soap' && (
                  <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-slate-900 text-white font-medium shrink-0" title={`Mode: ${s.mode}`}>
                    {s.mode === 'mental' ? 'MH' : s.mode === 'soap+obgyn' ? 'OB' : s.mode === 'soap+peds' ? 'Peds' : s.mode === 'surgical' ? 'Surg' : ''}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SpecialtySwitcher;
